import { InputTooltip } from '@components';
import { Close } from '@mui/icons-material';
import {
  Box,
  CircularProgress,
  IconButton,
  InputAdornment,
  Popover,
  TextField,
  Typography,
  styled,
  useTheme,
} from '@mui/material';
import { ArrowDown2, SearchNormal1 } from 'iconsax-react';
import { type FC, type ReactElement, useEffect, useRef, useState } from 'react';

import type { EntityType } from '@core/types/search';
import { hasStringValue, hasValue } from '@core/utils/equality';
import { SEARCH_DROPDOWN_MAX_WIDTH } from '@web/constants/app';

import { ENTITY_CONFIGS } from './entityConfig';
import { EntitySearchResults } from './EntitySearchResults';
import { EntitySearchTabs } from './EntitySearchTabs';
import { useEntitySearch } from './useEntitySearch';

export type EntitySelection = {
  id: string;
  type: EntityType;
  name: string;
};

const TypeChip = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  height: 24,
  padding: '0 8px',
  borderRadius: 8,
  backgroundColor: theme.palette.SlabIndigo[100],
  fontSize: 13,
  color: theme.palette.SlabIndigo[700],
  whiteSpace: 'nowrap',
}));

export type EntitySearchInputBaseProps = {
  value: EntitySelection | null;
  onChange: (selection: EntitySelection | null) => void;
  entityTypes: EntityType[];
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  error?: string;
  tip?: string;
  limitPerEntity?: number;
  onBlur?: () => void;
};

export const EntitySearchInputBase: FC<EntitySearchInputBaseProps> = ({
  value,
  onChange,
  entityTypes,
  label,
  placeholder = 'Search for a record by name or number',
  disabled = false,
  error,
  tip,
  limitPerEntity,
  onBlur,
}) => {
  const theme = useTheme();
  const anchorRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  const search = useEntitySearch({ entityTypes, limitPerEntity });

  const hasError = hasValue(error);

  useEffect(() => {
    if (isEditing) inputRef.current?.select();
  }, [isEditing]);

  const open = (): void => {
    if (disabled) return;
    setIsOpen(true);
    if (hasValue(value) && !isEditing) {
      setIsEditing(true);
      search.setQuery(value.name);
    }
    inputRef.current?.focus();
  };

  const close = ({ isBlur = true }: { isBlur?: boolean } = {}): void => {
    setIsOpen(false);
    setIsEditing(false);
    search.clearQuery();
    if (isBlur) onBlur?.();
  };

  const clear = (): void => {
    onChange(null);
    setIsEditing(false);
    search.clearQuery();
  };

  const isShrunk = isOpen || hasValue(value) || hasStringValue(search.query);
  const isLabelRaised = isShrunk || !hasValue(label);

  const labelWithTip = !hasValue(label) ? null : (
    <Box display='flex' gap='0.25rem' alignItems='center'>
      {label}
      {hasValue(tip) ? <InputTooltip tip={tip} /> : null}
    </Box>
  );

  const renderSelection = (): ReactElement | null => {
    if (!hasValue(value) || isEditing) {
      return null;
    }
    const config = ENTITY_CONFIGS[value.type];
    const { Icon } = config;

    return (
      <>
        <Typography noWrap sx={{ fontSize: 14, color: theme.palette.SlabGray[900] }}>
          {value.name}
        </Typography>
        <TypeChip>
          <Icon size={16} variant='TwoTone' color={theme.palette.SlabIndigo[700]} />
          {config.label}
        </TypeChip>
      </>
    );
  };

  return (
    <Box sx={{ width: '100%' }}>
      <TextField
        ref={anchorRef}
        inputRef={inputRef}
        fullWidth
        disabled={disabled}
        label={labelWithTip}
        value={search.query}
        onChange={(e): void => {
          const text = e.target.value;
          search.setQuery(text);
          if (!hasStringValue(text) && hasValue(value)) clear();
        }}
        onClick={open}
        onFocus={open}
        onMouseDown={(e): void => {
          if (!disabled && hasValue(value) && !isEditing) {
            e.preventDefault();
            open();
          }
        }}
        onKeyDown={(e): void => {
          if (e.key === 'Escape') {
            close();
          }
        }}
        error={hasError}
        helperText={hasError ? error : ' '}
        placeholder={!hasValue(value) && isLabelRaised ? placeholder : undefined}
        InputLabelProps={{ shrink: isShrunk }}
        InputProps={{
          startAdornment: isLabelRaised ? (
            <InputAdornment position='start' sx={{ gap: '0.5rem', maxWidth: '75%' }}>
              <SearchNormal1 size={20} variant='TwoTone' color={theme.palette.SlabIndigo[500]} />
              {renderSelection()}
            </InputAdornment>
          ) : undefined,
          endAdornment: (
            <InputAdornment position='end'>
              {hasValue(value) && !disabled && (
                <IconButton
                  size='small'
                  aria-label={`Clear ${label ?? 'selection'}`}
                  onMouseDown={(e): void => {
                    // Keep focus off the input: onFocus would re-open the dropdown before the click lands.
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  onClick={(e): void => {
                    e.stopPropagation();
                    clear();
                  }}
                >
                  <Close sx={{ fontSize: 22, color: theme.palette.SlabGray[600] }} />
                </IconButton>
              )}
              <ArrowDown2
                size={20}
                variant='TwoTone'
                color={theme.palette.SlabIndigo[500]}
                style={{ transform: isOpen ? 'rotate(180deg)' : undefined, flexShrink: 0 }}
              />
            </InputAdornment>
          ),
        }}
        sx={{ cursor: disabled ? 'default' : 'text' }}
      />

      <Popover
        open={isOpen}
        anchorEl={anchorRef.current}
        onClose={(): void => close()}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        disableAutoFocus
        disableEnforceFocus
        disableRestoreFocus
        slotProps={{
          paper: {
            sx: {
              mt: 0.5,
              width: anchorRef.current?.offsetWidth,
              maxWidth: SEARCH_DROPDOWN_MAX_WIDTH,
              borderRadius: '8px',
              border: `1px solid ${theme.palette.SlabGray.Stroke}`,
              boxShadow: '0 3px 10px rgba(96,108,128,0.06)',
            },
          },
        }}
      >
        <Box sx={{ maxHeight: 360, overflowY: 'auto' }}>
          <EntitySearchTabs
            entityTypes={entityTypes}
            activeTab={search.activeTab}
            onChange={search.setActiveTab}
            getCount={search.getCount}
            totalCount={search.totalCount}
          />

          {!search.hasQuery && (
            <Typography
              sx={{ color: theme.palette.SlabGray[600], fontSize: 14, textAlign: 'center', py: 3 }}
            >
              Start typing to search
            </Typography>
          )}

          {search.hasQuery && search.isLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress size={24} sx={{ color: theme.palette.SlabIndigo[500] }} />
            </Box>
          )}

          {search.hasQuery && !search.isLoading && search.isError && (
            <Typography
              sx={{ color: theme.palette.SlabGray[600], fontSize: 14, textAlign: 'center', p: 3 }}
            >
              Search is temporarily unavailable. Please try again.
            </Typography>
          )}

          {search.hasQuery &&
            !search.isLoading &&
            !search.isError &&
            hasValue(search.searchResults) && (
              <EntitySearchResults
                results={search.searchResults}
                entityTypes={entityTypes}
                activeTab={search.activeTab}
                onSelect={(type, item): void => {
                  onChange({ id: item.id, type, name: ENTITY_CONFIGS[type].getName(item) });
                  close({ isBlur: false });
                  inputRef.current?.blur();
                }}
              />
            )}
        </Box>
      </Popover>
    </Box>
  );
};
